import { useEffect, useMemo, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { LayoutDashboard, Plus, Save } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { Spinner } from '../lib/loader/loader'
import { SortableStoreSection } from '../components/store-design/SortableStoreSection'
import { storeDesignService, type StoreHomeComponent, type StoreHomeComponentType } from '../services/store-design.service'
import { bannerService, type StoreBanner } from '../services/banner.service'
import { carouselService, type StoreProductCarousel } from '../services/carousel.service'
import { useSessionStore } from '../store/auth/auth.store'
import { promotionSectionService } from '../services/promotion-section.service'

type SupportedSectionType = 'HERO' | 'SINGLE_BANNER' | 'BANNER_GRID_3' | 'PRODUCT_CAROUSEL' | 'PROMOTION_SECTION_CARDS' | 'PURCHASE_BENEFITS'

const sectionOptions: Array<{ value: SupportedSectionType; label: string }> = [
  { value: 'HERO', label: 'Banner principal' },
  { value: 'SINGLE_BANNER', label: 'Banner individual' },
  { value: 'BANNER_GRID_3', label: 'Banner 3 espacios' },
  { value: 'PRODUCT_CAROUSEL', label: 'Carrusel de productos' },
  { value: 'PROMOTION_SECTION_CARDS', label: 'Sección de promociones' },
  { value: 'PURCHASE_BENEFITS', label: 'Beneficios por compra' },
]

export function StoreDesign() {
  const canCreate = useSessionStore((state) => state.canAction('ecommerce.home_component.create'))
  const canEdit = useSessionStore((state) => state.canAction('ecommerce.home_component.edit'))
  const canDelete = useSessionStore((state) => state.canAction('ecommerce.home_component.delete'))

  const [components, setComponents] = useState<StoreHomeComponent[]>([])
  const [banners, setBanners] = useState<StoreBanner[]>([])
  const [carousels, setCarousels] = useState<StoreProductCarousel[]>([])
  const [promotionSections, setPromotionSections] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [savingOrder, setSavingOrder] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [type, setType] = useState<SupportedSectionType>('HERO')
  const [resourceId, setResourceId] = useState('')
  const [removingId, setRemovingId] = useState<number | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const [configured, bannersResponse, carouselsResponse, promoSectionsResponse] = await Promise.all([
        storeDesignService.getComponents(),
        bannerService.getBanners({ per_page: 100 }),
        carouselService.getCarousels({ per_page: 100 }),
        promotionSectionService.getSections({ per_page: 100 }),
      ])
      setComponents(configured)
      setBanners(bannersResponse.data.data)
      setCarousels(carouselsResponse.data.data)
      setPromotionSections(promoSectionsResponse.data.data)
    } catch {
      toast.error('No se pudo cargar el diseño de la tienda')
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])
  useEffect(() => { setResourceId('') }, [type])

  const isPurchaseBenefits = type === 'PURCHASE_BENEFITS'
  const alreadyHasPurchaseBenefits = components.some(c => c.component_type === 'PURCHASE_BENEFITS')

  const resources = useMemo(() => {
    if (type === 'PURCHASE_BENEFITS') return []
    if (type === 'PRODUCT_CAROUSEL') return carousels.map(item => ({ id: item.id, name: item.name }))
    if (type === 'PROMOTION_SECTION_CARDS') return promotionSections.map(item => ({ id: item.id, name: item.name }))
    return banners.filter(item => item.type === type).map(item => ({ id: item.id, name: item.name }))
  }, [type, banners, carousels, promotionSections])

  const displayName = (component: StoreHomeComponent) => {
    if (component.component_type === 'PURCHASE_BENEFITS') return 'Beneficios por compra'
    if (component.component_type === 'PRODUCT_CAROUSEL') return carousels.find(item => item.id === component.component_id)?.name ?? `Carrusel #${component.component_id}`
    if (component.component_type === 'PROMOTION_SECTION_CARDS') return promotionSections.find(item => item.id === component.component_id)?.name ?? `Sección Promociones #${component.component_id}`
    return banners.find(item => item.id === component.component_id)?.name ?? `Banner #${component.component_id}`
  }

  const moveSection = (from: number, to: number) => setComponents(current => {
    const copy = [...current]
    const [section] = copy.splice(from, 1)
    copy.splice(to, 0, section)
    return copy.map((item, index) => ({ ...item, sort_order: index }))
  })

  const saveOrder = async () => {
    if (!canEdit) return

    setSavingOrder(true)
    try {
      const updated = await storeDesignService.reorderComponents(components.map(({ id, sort_order }) => ({ id, sort_order })))
      setComponents(updated)
      toast.success('Orden de secciones guardado')
    } catch { toast.error('No se pudo guardar el orden') } finally { setSavingOrder(false) }
  }

  const create = async () => {
    if (!canCreate) return
    // Singleton: no require resource selection
    if (!isPurchaseBenefits && !resourceId) return
    try {
      const component = await storeDesignService.createComponent({
        component_type: type,
        component_id: isPurchaseBenefits ? 0 : Number(resourceId),
        sort_order: components.length,
        is_active: true,
      })
      setComponents(current => [...current, component])
      setDialogOpen(false)
      toast.success('Sección agregada al inicio de la tienda')
    } catch { toast.error('No se pudo agregar la sección') }
  }

  const toggle = async (component: StoreHomeComponent) => {
    if (!canEdit) return

    try {
      const updated = await storeDesignService.updateComponent(component.id, { is_active: !component.is_active })
      setComponents(current => current.map(item => item.id === updated.id ? updated : item))
    } catch { toast.error('No se pudo actualizar la visibilidad') }
  }

  const remove = async () => {
    if (!canDelete || removingId === null) return
    try {
      await storeDesignService.deleteComponent(removingId)
      const remaining = components.filter(item => item.id !== removingId).map((item, index) => ({ ...item, sort_order: index }))
      const updated = remaining.length
        ? await storeDesignService.reorderComponents(remaining.map(({ id, sort_order }) => ({ id, sort_order })))
        : remaining
      setComponents(updated)
      toast.success('Sección eliminada del diseño')
    } catch { toast.error('No se pudo quitar la sección') } finally { setRemovingId(null) }
  }

  return <div className="space-y-6 p-6">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div><h1 className="text-2xl font-semibold text-slate-900">Diseño de la tienda</h1><p className="mt-1 text-sm text-slate-600">Organiza las secciones que verán tus clientes en la página principal.</p></div>
      <div className="flex gap-2">
        {canCreate && (
          <Button variant="outline" onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Agregar sección
          </Button>
        )}

        {canEdit && (
          <Button
            onClick={saveOrder}
            disabled={savingOrder || components.length < 2}
          >
            <Save className="mr-2 h-4 w-4" />
            {savingOrder ? 'Guardando...' : 'Guardar orden'}
          </Button>
        )}
      </div>
    </div>

    <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-card-foreground">Arrastra las secciones para cambiar el orden en que aparecerán. Puedes ocultarlas temporalmente sin perder su configuración.</div>

    {loading ? <div className="flex justify-center py-16"><Spinner className="h-8 w-8" /></div> : components.length === 0 ? <div className="rounded-lg border border-dashed bg-card py-14 text-center"><LayoutDashboard className="mx-auto mb-3 h-9 w-9 text-muted-foreground" /><p className="font-medium">Aún no hay secciones configuradas</p><p className="mt-1 text-sm text-muted-foreground">Agrega un banner o carrusel para construir la página principal.</p>{canCreate && (<Button className="mt-4" onClick={() => setDialogOpen(true)}><Plus className="mr-2 h-4 w-4" />Agregar primera sección</Button>)}</div> : <DndProvider backend={HTML5Backend}><div className="space-y-3">{components.map((component, index) => <SortableStoreSection key={component.id} component={component} title={displayName(component)} index={index} moveSection={moveSection} onToggle={() => toggle(component)} onRemove={() => setRemovingId(component.id)} canEdit={canEdit}
canDelete={canDelete}
/>)}</div></DndProvider>}

    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar sección</DialogTitle>
          <DialogDescription>Elige el contenido que quieres mostrar en la página principal.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Tipo de sección */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Tipo de sección</label>
            <Select value={type} onValueChange={value => setType(value as SupportedSectionType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {sectionOptions.map(option => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.value === 'PURCHASE_BENEFITS' && alreadyHasPurchaseBenefits}
                  >
                    {option.label}
                    {option.value === 'PURCHASE_BENEFITS' && alreadyHasPurchaseBenefits && ' (ya agregado)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contenido: singleton vs. recurso seleccionable */}
          {isPurchaseBenefits ? (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              {alreadyHasPurchaseBenefits
                ? 'Ya existe un componente de Beneficios por compra. Solo puede haber uno por tienda.'
                : 'El contenido de esta sección se configura en Ajustes → Tienda → Beneficios por compra. Asegúrate de haberlo configurado antes de activarlo.'}
            </div>
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium">Contenido</label>
              <Select value={resourceId} onValueChange={setResourceId}>
                <SelectTrigger>
                  <SelectValue placeholder={resources.length ? 'Selecciona una opción' : 'No hay opciones disponibles'} />
                </SelectTrigger>
                <SelectContent>
                  {resources.map(resource => (
                    <SelectItem key={resource.id} value={String(resource.id)}>{resource.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {resources.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Crea primero {type === 'PRODUCT_CAROUSEL' ? 'un carrusel' : type === 'PROMOTION_SECTION_CARDS' ? 'una sección de promociones' : 'un banner de este tipo'}.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={create}
            disabled={isPurchaseBenefits ? alreadyHasPurchaseBenefits : !resourceId}
          >
            Agregar sección
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <ConfirmationModal isOpen={removingId !== null} onClose={() => setRemovingId(null)} onConfirm={remove} preset="delete" itemName="sección" />
  </div>
}
